import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Icon } from '@/components/common/Icon';
import {
  useApprovePetChangeRequestMutation,
  usePendingWorkspacePetChangeRequestQuery,
  useRejectPetChangeRequestMutation,
} from '@/hooks/usePetChangeRequests';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { PetChangeRequestReviewDialog } from './PetChangeRequestReviewDialog';

interface PetChangeRequestSectionProps {
  patientId: string;
}

export function PetChangeRequestSection({ patientId }: PetChangeRequestSectionProps) {
  const { t } = useTranslation(['patients', 'common']);
  const pendingRequestQuery = usePendingWorkspacePetChangeRequestQuery(patientId);
  const pendingRequest = pendingRequestQuery.data === undefined ? null : pendingRequestQuery.data;
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const approveMutation = useApprovePetChangeRequestMutation();
  const rejectMutation = useRejectPetChangeRequestMutation();

  if (pendingRequest === null) {
    return null;
  }

  function approve() {
    if (pendingRequest === null) {
      return;
    }
    approveMutation.mutate(pendingRequest.id, {
      onSuccess: function done() {
        showSuccessNotification(t('patients:changeRequest.approved'));
        setIsReviewOpen(false);
      },
      onError: function fail(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(
          message.length > 0 ? message : t('patients:changeRequest.approveError')
        );
      },
    });
  }

  function reject(reviewNote: string) {
    if (pendingRequest === null) {
      return;
    }
    rejectMutation.mutate(
      { requestId: pendingRequest.id, reviewNote },
      {
        onSuccess: function done() {
          showSuccessNotification(t('patients:changeRequest.rejected'));
          setIsReviewOpen(false);
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(
            message.length > 0 ? message : t('patients:changeRequest.rejectError')
          );
        },
      }
    );
  }

  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={function openReview() {
          setIsReviewOpen(true);
        }}
        className="w-full flex items-center gap-3 rounded-xl bg-zoowarningbg px-4 py-3 text-left hover:brightness-95 transition-all"
      >
        <span className="w-9 h-9 rounded-lg bg-zoowhite text-zoowarningtext flex items-center justify-center shrink-0">
          <Icon name="Edit" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-zoowarningtext">
            {t('patients:changeRequest.bannerTitle')}
          </span>
          <span className="block text-xs text-zoowarningtext text-opacity-80">
            {t('patients:changeRequest.bannerHint')}
          </span>
        </span>
        <Icon name="ChevronRight" className="text-zoowarningtext shrink-0" />
      </button>

      {isReviewOpen === true ? (
        <PetChangeRequestReviewDialog
          request={pendingRequest}
          isApproving={approveMutation.isPending}
          isRejecting={rejectMutation.isPending}
          onApprove={approve}
          onReject={reject}
          onClose={function close() {
            setIsReviewOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
